import { subject } from '@casl/ability';

import { useAbility } from '@/shared/features/ability/context/ability-context';
import {
  TAction,
  TConditionAttribute,
  TEntityActionType,
  TPermissionCheckProps,
} from '@/shared/features/ability/hooks/use-user-ability/use-user-ability.types';
import { useLinkedTagsForAsset } from '@/shared/features/tags/hooks/use-linked-tags-for-asset/use-linked-tags-for-asset';
import { useLinkedTagsForAssets } from '@/shared/features/tags/hooks/use-linked-tags-for-assets/use-linked-tags-for-assets';
import { AssetTypeEnum } from '@/shared/types/types.generated';

const ENTITY_TO_ASSET_TYPE: Partial<Record<TEntityActionType, AssetTypeEnum>> =
  {
    Tag: AssetTypeEnum.Tag,
    License: AssetTypeEnum.License,
    Document: AssetTypeEnum.Document,
    Pipeline: AssetTypeEnum.Pipeline,
    ResourceGroup: AssetTypeEnum.ResourceGroup,
    Project: AssetTypeEnum.Project,
    Device: AssetTypeEnum.Device,
    Gateway: AssetTypeEnum.Gateway,
    Vault: AssetTypeEnum.Vault,
    Secret: AssetTypeEnum.Secret,
  };

const toAssetType = (
  entity?: TEntityActionType | TEntityActionType[],
): AssetTypeEnum | undefined => {
  if (!entity || Array.isArray(entity)) {
    return undefined;
  }

  return ENTITY_TO_ASSET_TYPE[entity];
};

const toArray = <T>(value: T | T[]): T[] =>
  Array.isArray(value) ? value : [value];

export const usePermissions = (permission?: TPermissionCheckProps): boolean => {
  const ability = useAbility();
  const { tagNames } = useLinkedTagsForAsset({
    assetId: permission?.attribute?.id,
    assetType: toAssetType(permission?.entity),
  });

  const { assetTags } = useLinkedTagsForAssets({
    assetIds: permission?.attributes?.map(({ id }) => id!),
    assetType: toAssetType(permission?.entity),
  });

  if (!permission || !permission.entity) {
    return true;
  }

  const { action, entity, attribute, attributes, relation = 'or' } = permission;
  const actions = toArray(action);
  const entities = toArray(entity);

  const checkPermissionForMultipleAttributes = (
    action: TAction,
    entity: TEntityActionType,
    attributes: TConditionAttribute[],
  ) => {
    let tagActionAllowed = false;

    if (assetTags.length > 0) {
      tagActionAllowed = attributes.every((attribute) => {
        const tags = assetTags.find(({ assetId }) => assetId === attribute.id);

        return ability.can(
          action,
          subject(entity, { attribute, tags: tags?.tags }),
        );
      });
    }

    let actionAllowed = attributes.every((attribute) =>
      ability.can(action, subject(entity, { attribute })),
    );

    return tagActionAllowed || actionAllowed;
  };

  const checkPermission = (
    action: TAction,
    entity: TEntityActionType,
  ): boolean => {
    let tagActionAllowed = false;
    let multipleActionAllowed = false;
    let actionAllowed = false;

    if (tagNames.length > 0) {
      tagActionAllowed = ability.can(
        action,
        subject(entity, { tags: tagNames }),
      );
    }

    if (attributes) {
      multipleActionAllowed = checkPermissionForMultipleAttributes(
        action,
        entity,
        attributes,
      );
    }

    if (attribute) {
      actionAllowed = ability.can(action, subject(entity, { attribute }));
    }

    return (
      ability.can(action, entity) ||
      actionAllowed ||
      multipleActionAllowed ||
      tagActionAllowed
    );
  };

  const results = actions.flatMap((action) =>
    entities.map((entity) => checkPermission(action, entity)),
  );

  return relation === 'or' ? results.some(Boolean) : results.every(Boolean);
};
